import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Plus, ShieldAlert, Trash2, Edit2, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FormField } from "@/components/common/FormSection";
import { SearchableSelect } from "@/components/common/SearchableSelect";
import { api, formatApiError } from "@/lib/api";

/* Curated suggestions — guide entry while still allowing free text (string stored as-is). */
const ALLERGEN_SUGGESTIONS = [
  "Penicillin", "Amoxicillin", "Cephalosporins", "Sulfa drugs (Sulfonamides)", "Aspirin",
  "NSAIDs", "Ibuprofen", "Codeine", "Morphine", "Contrast dye (Iodinated)", "Latex",
  "Peanuts", "Tree nuts", "Shellfish", "Eggs", "Milk", "Soy", "Wheat / Gluten",
  "Bee / wasp stings", "Pollen", "Dust mites", "Animal dander",
];
const REACTION_SUGGESTIONS = [
  "Anaphylaxis", "Hives / Urticaria", "Rash", "Itching / Pruritus", "Swelling / Angioedema",
  "Difficulty breathing / Bronchospasm", "Wheezing", "Nausea", "Vomiting", "Diarrhea",
  "Abdominal pain", "Dizziness", "Hypotension", "Fever", "Throat tightness",
];
const ONSET_SUGGESTIONS = ["Childhood", "Adolescence", "Adulthood", "Recent", "Unknown"];

/* ============================================================================
 * Severity styling
 * ==========================================================================*/
export const ALLERGY_SEVERITY = {
  life_threatening: { label: "Life-threatening", bg: "#FEE2E2", text: "#991B1B", icon: ShieldAlert, accent: "#DC2626" },
  severe:           { label: "Severe",           bg: "#FFEDD5", text: "#9A3412", icon: AlertTriangle, accent: "#EA580C" },
  moderate:         { label: "Moderate",         bg: "#FEF3C7", text: "#92400E", icon: AlertTriangle, accent: "#D97706" },
  mild:             { label: "Mild",             bg: "#E0E7FF", text: "#3730A3", icon: Activity, accent: "#6366F1" },
};

const ALLERGEN_TYPE_LABELS = {
  drug: "Drug", food: "Food", environmental: "Environmental", latex: "Latex", other: "Other",
};

/* ============================================================================
 * Banner — sits at the top of the patient profile if any active allergies
 * ==========================================================================*/
export function AllergiesBanner({ allergies = [] }) {
  if (!allergies.length) {
    return (
      <div
        className="hah-card p-2.5 flex items-center gap-2 border-l-4 border-l-[#10B981] bg-[#ECFDF5] mb-3"
        data-testid="cl-allergies-banner-clear"
      >
        <ShieldAlert className="w-4 h-4 text-[#10B981]" />
        <span className="text-xs font-semibold text-[#065F46] uppercase tracking-wider">
          No Known Allergies
        </span>
      </div>
    );
  }
  const top = allergies[0];
  const cfg = ALLERGY_SEVERITY[top.severity] || ALLERGY_SEVERITY.moderate;
  const Icon = cfg.icon;
  return (
    <div
      className="hah-card p-2.5 flex items-center gap-3 border-l-4 mb-3"
      style={{ borderColor: cfg.accent, background: cfg.bg }}
      data-testid="cl-allergies-banner"
    >
      <Icon className="w-5 h-5 shrink-0" style={{ color: cfg.accent }} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: cfg.text }}>
          Allergy Alert · {allergies.length} active
        </div>
        <div className="text-sm font-semibold truncate" style={{ color: cfg.text }}>
          {allergies.slice(0, 4).map((a) => `${a.allergen} (${ALLERGY_SEVERITY[a.severity]?.label || a.severity})`).join(" · ")}
          {allergies.length > 4 ? ` · +${allergies.length - 4} more` : ""}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * Allergies tab — list / add / edit / soft-delete
 * ==========================================================================*/
const EMPTY_FORM = {
  allergen: "", allergen_type: "drug", severity: "moderate",
  reaction: "", onset: "", status: "active", notes: "",
};

export function AllergiesTab({ patientId, onChange, readOnly = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/patients/${patientId}/allergies`, {
        params: { include_resolved: showResolved },
      });
      setItems(r.data.items || []);
      onChange?.(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [patientId, showResolved, onChange]);

  useEffect(() => { load(); }, [load]);

  function startCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpenDialog(true);
  }

  function startEdit(a) {
    setEditing(a);
    setForm({
      allergen: a.allergen || "", allergen_type: a.allergen_type || "drug",
      severity: a.severity || "moderate", reaction: a.reaction || "",
      onset: a.onset || "", status: a.status || "active", notes: a.notes || "",
    });
    setOpenDialog(true);
  }

  async function save() {
    if (!form.allergen.trim()) { toast.error("Allergen is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/allergies/${editing.id}`, form);
        toast.success("Allergy updated");
      } else {
        await api.post(`/patients/${patientId}/allergies`, form);
        toast.success("Allergy recorded");
      }
      setOpenDialog(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  async function softDelete(a) {
    if (!window.confirm(`Mark "${a.allergen}" as resolved?`)) return;
    try {
      await api.delete(`/allergies/${a.id}`);
      toast.success("Allergy marked resolved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="cl-allergies-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-[#1F2937]">Allergies & Intolerances</h3>
          <span className="text-xs text-[#6B7280]">{items.length} {showResolved ? "total" : "active"}</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-[#475569] select-none cursor-pointer" data-testid="cl-allergies-toggle-resolved-wrap">
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
              className="rounded"
              data-testid="cl-allergies-toggle-resolved"
            />
            Show resolved
          </label>
          {!readOnly && (
            <Button
              onClick={startCreate}
              className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
              data-testid="cl-allergies-add"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add Allergy
            </Button>
          )}
        </div>
      </div>

      <div className="hah-card overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-[#F5F7FA] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="text-left p-2.5">Severity</th>
              <th className="text-left p-2.5">Allergen</th>
              <th className="text-left p-2.5">Type</th>
              <th className="text-left p-2.5">Reaction</th>
              <th className="text-left p-2.5">Status</th>
              <th className="text-left p-2.5">Recorded</th>
              <th className="text-right p-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => {
              const cfg = ALLERGY_SEVERITY[a.severity] || ALLERGY_SEVERITY.moderate;
              const Icon = cfg.icon;
              return (
                <tr key={a.id} className="border-t border-[#E5E7EB] hover:bg-[#FAFBFC]" data-testid={`cl-allergy-row-${a.id}`}>
                  <td className="p-2.5">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold"
                      style={{ background: cfg.bg, color: cfg.text }}
                    >
                      <Icon className="w-3 h-3" />
                      {cfg.label}
                    </span>
                  </td>
                  <td className="p-2.5 font-semibold text-[#1F2937]">{a.allergen}</td>
                  <td className="p-2.5 text-[#475569] text-xs">{ALLERGEN_TYPE_LABELS[a.allergen_type] || a.allergen_type}</td>
                  <td className="p-2.5 text-[#475569]">{a.reaction || "—"}</td>
                  <td className="p-2.5 text-xs">
                    <span
                      className="px-1.5 py-0.5 rounded text-[10px] uppercase font-bold"
                      style={{
                        background: a.status === "active" ? "#FEF3C7" : "#E5E7EB",
                        color: a.status === "active" ? "#92400E" : "#475569",
                      }}
                    >
                      {a.status}
                    </span>
                  </td>
                  <td className="p-2.5 text-[#6B7280] text-xs">
                    {a.recorded_by_name || "—"}<br/>
                    <span className="text-[10px]">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}</span>
                  </td>
                  <td className="p-2.5 text-right">
                    {!readOnly && (
                      <Button size="sm" variant="ghost" onClick={() => startEdit(a)} className="h-7 px-2" data-testid={`cl-allergy-edit-${a.id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {!readOnly && a.status === "active" && (
                      <Button size="sm" variant="ghost" onClick={() => softDelete(a)} className="h-7 px-2 text-[#991B1B]" data-testid={`cl-allergy-delete-${a.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {readOnly && <span className="text-xs text-[#94A3B8]">—</span>}
                  </td>
                </tr>
              );
            })}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-sm text-[#6B7280]">
                No {showResolved ? "" : "active "}allergies recorded for this patient.
              </td></tr>
            )}
          </tbody>
        </table></div>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Allergy" : "Add Allergy"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2" data-testid="cl-allergy-form">
            <FormField label="Allergen" required>
              <SearchableSelect
                value={form.allergen}
                onChange={(v) => setForm({ ...form, allergen: v })}
                options={ALLERGEN_SUGGESTIONS.map((s) => ({ value: s, label: s }))}
                allowCustom
                placeholder="Select or search an allergen…"
                testid="cl-allergy-form-allergen"
              />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Type">
                <Select value={form.allergen_type} onValueChange={(v) => setForm({ ...form, allergen_type: v })}>
                  <SelectTrigger data-testid="cl-allergy-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ALLERGEN_TYPE_LABELS).map(([v, l]) =>
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Severity">
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="cl-allergy-form-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ALLERGY_SEVERITY).map(([v, l]) =>
                      <SelectItem key={v} value={v}>{l.label}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </FormField>
            </div>
            <FormField label="Reaction">
              <SearchableSelect
                value={form.reaction}
                onChange={(v) => setForm({ ...form, reaction: v })}
                options={REACTION_SUGGESTIONS.map((s) => ({ value: s, label: s }))}
                allowCustom
                placeholder="Select or search a reaction…"
                testid="cl-allergy-form-reaction"
              />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Onset">
                <SearchableSelect
                  value={form.onset}
                  onChange={(v) => setForm({ ...form, onset: v })}
                  options={ONSET_SUGGESTIONS.map((s) => ({ value: s, label: s }))}
                  allowCustom
                  placeholder="Select or type onset…"
                  testid="cl-allergy-form-onset"
                />
              </FormField>
              <FormField label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="cl-allergy-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </div>
            <FormField label="Notes">
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="cl-allergy-form-notes"
              />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cl-allergy-form-save">
              {saving ? "Saving…" : (editing ? "Save changes" : "Record allergy")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================================================================
 * Manual Vitals dialog — clinician-recorded readings
 * ==========================================================================*/
const VITAL_FIELDS = [
  { key: "bp_systolic",      label: "BP Systolic",     unit: "mmHg", step: 1, min: 50, max: 250 },
  { key: "bp_diastolic",     label: "BP Diastolic",    unit: "mmHg", step: 1, min: 30, max: 150 },
  { key: "heart_rate",       label: "Heart Rate",      unit: "bpm",  step: 1, min: 30, max: 220 },
  { key: "spo2",             label: "SpO₂",            unit: "%",    step: 1, min: 50, max: 100 },
  { key: "temperature",      label: "Temperature",     unit: "°C",   step: 0.1, min: 30, max: 43 },
  { key: "respiratory_rate", label: "Respiratory Rate",unit: "rpm",  step: 1, min: 4,  max: 60 },
];

export function ManualVitalsDialog({ patientId, open, onOpenChange, onSubmitted }) {
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setValues({});
      setNotes("");
    }
  }, [open]);

  async function submit() {
    const readings = VITAL_FIELDS
      .filter((f) => values[f.key] !== "" && values[f.key] != null)
      .map((f) => {
        const v = parseFloat(values[f.key]);
        if (Number.isNaN(v)) return null;
        if (v < f.min || v > f.max) return { _err: `${f.label}: out of range (${f.min}–${f.max})` };
        return { vital_type: f.key, value: v, unit: f.unit };
      })
      .filter(Boolean);
    if (readings.some((r) => r._err)) {
      toast.error(readings.find((r) => r._err)._err);
      return;
    }
    if (readings.length === 0) {
      toast.error("Enter at least one vital");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/rpm/patients/${patientId}/manual-vitals`, { readings, notes: notes || null });
      const alertsN = r.data.alerts_created?.length || 0;
      if (alertsN) toast.success(`Saved · ${alertsN} new alert${alertsN === 1 ? "" : "s"} fired`);
      else toast.success(`Recorded ${readings.length} vital${readings.length === 1 ? "" : "s"}`);
      onSubmitted?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="cl-manual-vitals-dialog">
        <DialogHeader>
          <DialogTitle>Record Vitals</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-xs text-[#6B7280]">
            Enter the readings observed. Values feed NEWS2 scoring and trigger alerts if the patient
            is RPM-enrolled.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {VITAL_FIELDS.map((f) => (
              <FormField key={f.key} label={`${f.label} (${f.unit})`}>
                <Input
                  type="number"
                  step={f.step}
                  min={f.min}
                  max={f.max}
                  value={values[f.key] ?? ""}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  placeholder="—"
                  data-testid={`cl-manual-vitals-${f.key}`}
                />
              </FormField>
            ))}
          </div>
          <FormField label="Notes">
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Context for this reading…"
              data-testid="cl-manual-vitals-notes"
            />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5E7EB]">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cl-manual-vitals-save">
            {saving ? "Saving…" : "Save vitals"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
